<template>
  <modal
    name="sql-motivo-alteracao"
    height="auto"
    width="94%"
    :adaptive="true"
    :maxWidth="640"
    :lazy="true"
    :scrollable="true"
    :clickToClose="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <CloseModal :close="cancel" />
    <div class="modalbox">
      <div class="modalbox-content motivo-box">
        <h5 class="motivo-title">Motivo da alteração</h5>

        <!-- Aviso de alteração em produção -->
        <div v-if="isProducao" class="alert alert-danger">
          <fa-icon icon="exclamation-triangle" class="mr-1" />
          <span>
            Você está alterando uma consulta em <strong>PRODUÇÃO</strong>.
            Esta é uma operação controlada e auditável.
          </span>
        </div>

        <!-- Usuário interno Versotech: motivo guiado, sem escolha de tipo.
             Produção -> Ticket; Implantação -> dados da implantação. -->
        <template v-if="ehVersotech">
          <!-- Produção: Ticket -->
          <div v-if="tipo === 'ticket'" class="form-group">
            <label class="motivo-label">Número/identificador do ticket <span class="req">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model.trim="ticket"
              placeholder="Ex.: SUP-1234"
            />
          </div>

          <!-- Implantação: etapa + solicitante -->
          <template v-else-if="tipo === 'cliente_implantacao'">
            <div class="form-group">
              <label class="motivo-label">Etapa alterada <span class="req">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model.trim="etapa"
                placeholder="Ex.: Entrega 03 - Integração"
              />
            </div>
            <div class="form-group">
              <label class="motivo-label">Quem solicitou <span class="req">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model.trim="solicitante"
                placeholder="Nome de quem solicitou a alteração"
              />
            </div>
          </template>

          <div class="form-group">
            <label class="motivo-label">Descrição do motivo <span class="req">*</span></label>
            <textarea
              class="form-control"
              rows="3"
              v-model.trim="descricao"
              placeholder="Descreva o que foi alterado e por quê"
            ></textarea>
          </div>
        </template>

        <!-- Cliente (usuário próprio): apenas um campo de motivo livre, sem título. -->
        <div v-else class="form-group">
          <textarea
            class="form-control"
            rows="4"
            v-model.trim="descricao"
            placeholder="Descreva o motivo da alteração"
          ></textarea>
        </div>

        <div v-if="erro" class="alert alert-warning erro-msg">
          {{ erro }}
        </div>
      </div>
    </div>

    <div class="modal-footer-bar">
      <CButton variant="outline" color="secondary" @click="cancel">
        Cancelar
      </CButton>
      <CButton color="primary" @click="confirm">
        Confirmar e salvar
      </CButton>
    </div>
  </modal>
</template>

<script>
import CloseModal from "@/components/CloseModal";
import { getColaboradorLogado, isEmailInterno } from "@/helpers/utils";

export default {
  name: "ModalMotivoAlteracao",
  components: { CloseModal },
  data() {
    return {
      ambiente: "producao",
      viewName: "",
      ehVersotech: false,
      tipo: "ticket",
      ticket: "",
      etapa: "",
      solicitante: "",
      alteradoPor: "",
      descricao: "",
      erro: null,
      resolver: null,
    };
  },
  computed: {
    isProducao() {
      return this.ambiente === "producao";
    },
  },
  beforeDestroy() {
    // Redundância defensiva: se o componente morrer com o modal aberto.
    document.removeEventListener("keyup", this.handleEsc);
  },
  methods: {
    /**
     * Abre o modal e devolve uma Promise:
     *  - resolve(false) ao cancelar;
     *  - resolve({ motivo }) ao confirmar.
     *
     * O formulário se adapta a quem está logado:
     *  - usuário interno Versotech: motivo guiado, sem escolha de tipo —
     *    em produção pede o ticket, em implantação pede etapa/solicitante;
     *  - cliente (usuário próprio): apenas um campo de motivo livre.
     * "Quem alterou" é preenchido automaticamente a partir do colaborador logado.
     *
     * @param {{ ambiente?: string, viewName?: string }} opts
     */
    open(opts = {}) {
      this.ambiente = opts.ambiente || "producao";
      this.viewName = opts.viewName || "";

      const colaborador = getColaboradorLogado() || {};
      this.ehVersotech = isEmailInterno(colaborador.email);
      this.alteradoPor = this.formatarAlteradoPor(colaborador);

      // Interno: tipo definido pelo ambiente (produção -> ticket; senão implantação).
      // Cliente: motivo livre.
      if (!this.ehVersotech) {
        this.tipo = "cliente";
      } else {
        this.tipo = this.isProducao ? "ticket" : "cliente_implantacao";
      }

      this.ticket = "";
      this.etapa = "";
      this.solicitante = "";
      this.descricao = "";
      this.erro = null;

      // Restaura o que ficou digitado num cancelamento/Esc acidental anterior
      // desta mesma view (rascunho por view; some ao fechar a aba).
      this.carregarRascunho();

      this.$modal.show("sql-motivo-alteracao");

      return new Promise((resolve) => {
        this.resolver = resolve;
      });
    },
    formatarAlteradoPor(colaborador) {
      const nome = (colaborador && colaborador.nome) || "";
      const email = (colaborador && colaborador.email) || "";
      if (nome && email) {
        return `${nome} (${email})`;
      }
      return nome || email || "";
    },
    confirm() {
      this.erro = null;

      if (this.tipo === "ticket" && !this.ticket) {
        this.erro = "Informe o número/identificador do ticket.";
        return;
      }
      if (this.tipo === "cliente_implantacao" && (!this.etapa || !this.solicitante)) {
        this.erro = "Informe a etapa alterada e quem solicitou.";
        return;
      }
      if (!this.descricao) {
        this.erro = "Descreva o motivo da alteração.";
        return;
      }

      // Salvou de fato: o rascunho não é mais necessário.
      this.limparRascunho();

      this.finish({
        motivo: {
          tipo: this.tipo,
          descricao: this.descricao,
          alterado_por: this.alteradoPor,
          ticket: this.tipo === "ticket" ? this.ticket : null,
          etapa: this.tipo === "cliente_implantacao" ? this.etapa : null,
          solicitante: this.tipo === "cliente_implantacao" ? this.solicitante : null,
        },
      });
    },
    cancel() {
      // Guarda o digitado antes de fechar: cancelamento/Esc sem querer não perde
      // o motivo/ticket já preenchido (restaurado no próximo open desta view).
      this.salvarRascunho();
      this.finish(false);
    },
    // Esc cancela este modal (que é sempre o do topo quando aberto). O rascunho
    // é preservado pelo próprio cancel().
    onOpened() {
      document.addEventListener("keyup", this.handleEsc);
    },
    onClosed() {
      document.removeEventListener("keyup", this.handleEsc);
    },
    handleEsc(e) {
      if (e.key === "Escape") {
        this.cancel();
      }
    },
    rascunhoKey() {
      return `sql-motivo-rascunho:${this.viewName || "_"}`;
    },
    salvarRascunho() {
      const rascunho = {
        ticket: this.ticket,
        etapa: this.etapa,
        solicitante: this.solicitante,
        descricao: this.descricao,
      };
      const temConteudo = Object.keys(rascunho).some(
        (k) => String(rascunho[k] || "").trim()
      );
      try {
        if (temConteudo) {
          sessionStorage.setItem(this.rascunhoKey(), JSON.stringify(rascunho));
        } else {
          // Nada digitado: não deixa rascunho vazio para trás.
          sessionStorage.removeItem(this.rascunhoKey());
        }
      } catch (e) {
        /* sessionStorage indisponível: rascunho é best-effort */
      }
    },
    carregarRascunho() {
      try {
        const bruto = sessionStorage.getItem(this.rascunhoKey());
        if (!bruto) return;
        const r = JSON.parse(bruto) || {};
        this.ticket = r.ticket || "";
        this.etapa = r.etapa || "";
        this.solicitante = r.solicitante || "";
        this.descricao = r.descricao || "";
      } catch (e) {
        /* rascunho corrompido: ignora */
      }
    },
    limparRascunho() {
      try {
        sessionStorage.removeItem(this.rascunhoKey());
      } catch (e) {
        /* nada a fazer */
      }
    },
    finish(result) {
      this.$modal.hide("sql-motivo-alteracao");
      if (this.resolver) {
        this.resolver(result);
        this.resolver = null;
      }
    },
  },
};
</script>

<style scoped>
.motivo-box {
  padding: 8px 4px;
}

.motivo-title {
  font-weight: 600;
  margin-bottom: 16px;
}

.motivo-label {
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 4px;
  display: block;
}

.req {
  color: #e55353;
}

.erro-msg {
  margin-top: 8px;
  margin-bottom: 0;
}

.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>
